// src/lib/label.js
// ───────────────────────────────────────────────────────────────────────────
// 🏷️ Motor COMPARTIDO de etiquetas 4×6" (jsPDF + QR). Un solo motor para las
// tres etiquetas del sistema — retal (remnantLabel.js), material (materialLabel.js)
// y, a futuro, paquete — para no duplicar el header/logo/QR/layout.
// ───────────────────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";
import QRCode from "qrcode";

export const LABEL_W = 288; // 4 in (pt)
export const LABEL_H = 432; // 6 in (pt)
export const LABEL_M = 18;  // margen

/** Medida limpia para etiquetas (hasta 4 decimales). */
export const fmtDim = (v) => String(Math.round(Number(v) * 10000) / 10000);

/**
 * Texto seguro para jsPDF (helvetica = WinAnsi): la flecha "→" y otros unicode
 * fuera de Latin-1 salen como basura ("!'") y descuadran el texto. Se reemplazan
 * por equivalentes ASCII antes de imprimir.
 */
export const pdfText = (s) => String(s ?? '')
    .replace(/→/g, '-')      // → (locationLabel paleta → zona)
    .replace(/—|–/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x20-\xFF]/g, '');

/** Carga una imagen same-origin (ej. /public logo) como dataURL para jsPDF. */
export async function loadLocalImage(url) {
    try {
        const resp = await fetch(url);
        if (!resp.ok) return null;
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
            const fr = new FileReader();
            fr.onload = () => res(fr.result);
            fr.onerror = rej;
            fr.readAsDataURL(blob);
        });
        const m = /^data:image\/(\w+);/.exec(dataUrl || "");
        let fmt = (m ? m[1] : "png").toUpperCase();
        if (fmt === "JPG") fmt = "JPEG";
        const dims = await new Promise((res) => {
            const im = new Image();
            im.onload = () => res({ w: im.naturalWidth || 1, h: im.naturalHeight || 1 });
            im.onerror = () => res({ w: 1, h: 1 });
            im.src = dataUrl;
        });
        return { dataUrl, format: fmt, w: dims.w, h: dims.h };
    } catch (_) { return null; }
}

/**
 * Crea el doc 4×6", dibuja el logo CARTONBAR (o el texto de respaldo) + la línea
 * separadora, y devuelve el doc listo para que cada etiqueta pinte su cuerpo.
 *
 * @param {object} [opts]
 * @param {number} [opts.flap] alto en pt de una SOLAPA en blanco arriba (para
 *   pegar o meter entre las hojas de la estiba): se marca con línea punteada y
 *   el logo + todo el contenido arrancan DEBAJO de esa línea.
 * @return {Promise<{ doc: jsPDF, W:number, H:number, M:number, center:number, bodyY:number }>}
 *   bodyY = y donde empieza el cuerpo (debajo del logo y su línea).
 */
export async function newLabelDoc({ flap = 0 } = {}) {
    const doc = new jsPDF({ unit: "pt", format: [LABEL_W, LABEL_H] });
    const center = LABEL_W / 2;

    let top = LABEL_M;
    if (flap > 0) {
        // Línea punteada = borde de la solapa (lo de arriba queda en blanco).
        doc.setDrawColor(150); doc.setLineWidth(0.75);
        doc.setLineDashPattern([4, 3], 0);
        doc.line(0, flap, LABEL_W, flap);
        doc.setLineDashPattern([], 0);
        top = flap + 10;
    }

    const logo = await loadLocalImage(`${process.env.PUBLIC_URL || ""}/cartonbar-logo.png`);
    if (logo) {
        const lw = 130, lh = Math.min(40, lw * (logo.h / logo.w));
        try { doc.addImage(logo.dataUrl, logo.format, center - lw / 2, top, lw, lh); } catch (_) {}
    } else {
        doc.setFont("helvetica", "bold"); doc.setFontSize(14);
        doc.text("CARTONBAR", center, top + 16, { align: "center" });
    }
    doc.setDrawColor(0); doc.setLineWidth(1);
    doc.line(LABEL_M, top + 48, LABEL_W - LABEL_M, top + 48);
    return { doc, W: LABEL_W, H: LABEL_H, M: LABEL_M, center, bodyY: top + 48 };
}

/** Dibuja un QR (PNG) en el doc. Devuelve true si pudo. */
export async function drawQr(doc, payload, x, y, size) {
    try {
        const qr = await QRCode.toDataURL(payload, { margin: 1, width: 320 });
        doc.addImage(qr, "PNG", x, y, size, size);
        return true;
    } catch (_) { return false; }
}
